/* @jsx h */
import { defineComponent, h } from "vue";
import { RightSheet } from "@lemmonhq/uikit";
import { formatDate } from "@lemmonhq/orders";

export type ClosingReportDetailsProps = {
  report: any;
  isOpen: boolean;
  onClose: () => void;
  onDelete: (id: string) => void;
};

const formatCHF = (amount: number) =>
  new Intl.NumberFormat("de-CH", {
    style: "currency",
    currency: "CHF",
    minimumFractionDigits: 2,
  }).format(amount ?? 0);

const diffClass = (v: number) =>
  v > 0 ? "text-success" : v < 0 ? "text-error" : "text-base-content/40";

const METHOD_META: Record<string, { icon: string; label: string }> = {
  cash: { icon: "payments", label: "Cash" },
  card: { icon: "credit_card", label: "Card" },
  manual: { icon: "receipt_long", label: "Manual" },
};

const BalanceSection = ({
  title,
  balances,
  isDiff,
  methods,
}: {
  title: string;
  balances: Record<string, number>;
  isDiff?: boolean;
  methods: string[];
}) => (
  <div class="flex flex-col gap-2">
    <h4 class="text-xs font-semibold uppercase tracking-wider text-base-content/50">
      {title}
    </h4>
    <div class="flex flex-col gap-1.5">
      {methods.map((m) => {
        const meta = METHOD_META[m] ?? { icon: "payments", label: m };
        const amt = balances?.[m] ?? 0;
        return (
          <div key={m} class="flex items-center justify-between gap-3">
            <span class="flex items-center gap-1.5 text-sm text-base-content/70">
              <span class="material-symbols-outlined text-base">
                {meta.icon}
              </span>
              {meta.label}
            </span>
            <span
              class={`text-sm tabular-nums font-medium ${isDiff ? diffClass(amt) : "text-base-content"}`}
            >
              {formatCHF(amt)}
            </span>
          </div>
        );
      })}
    </div>
  </div>
);

export const ClosingReportDetails = defineComponent<ClosingReportDetailsProps>({
  name: "ClosingReportDetails",
  props: ["report", "isOpen", "onClose", "onDelete"],
  setup(props) {
    return () => {
      const r = props.report;
      return (
        <RightSheet
          isOpen={props.isOpen}
          onClose={props.onClose}
          width="820px"
          title={
            <div class="min-w-0 text-center lg:text-left">
              <div class="text-lg font-bold text-accent truncate">
                {r.shift?.user?.name ?? "Unknown"}
              </div>
              <div class="text-xs text-accent/60 truncate">
                {formatDate(r.shift?.start)} → {formatDate(r.shift?.end)}
              </div>
            </div>
          }
        >
          <div class="flex flex-col gap-6 p-4">
            {/* Meta */}
            <div class="flex items-center gap-2 text-xs text-base-content/50">
              <span class="material-symbols-outlined text-sm">
                calendar_today
              </span>
              Created {formatDate(r.created_at)}
            </div>

            {/* Balance grid */}
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-6 divide-y sm:divide-y-0 sm:divide-x divide-base-content/10">
              <BalanceSection
                title="Starting"
                balances={r.starting_balances}
                methods={["cash"]}
              />
              <div class="pt-4 sm:pt-0 sm:pl-6">
                <BalanceSection
                  title="Closing"
                  balances={r.closing_balances}
                  methods={["cash", "card", "manual"]}
                />
              </div>
              <div class="pt-4 sm:pt-0 sm:pl-6">
                <BalanceSection
                  title="Differences"
                  balances={r.differences}
                  methods={["cash", "card", "manual"]}
                  isDiff
                />
              </div>
            </div>

            {/* Notes */}
            {r.notes && (
              <div class="flex flex-col gap-1.5 border-t border-base-content/10 pt-4">
                <h4 class="text-xs font-semibold uppercase tracking-wider text-base-content/50 flex items-center gap-1.5">
                  <span class="material-symbols-outlined text-sm">notes</span>
                  Notes
                </h4>
                <p class="text-sm text-base-content/80 whitespace-pre-wrap">
                  {r.notes}
                </p>
              </div>
            )}

            {/* Delete */}
            <div class="border-t border-base-content/10 pt-4">
              <button
                class="btn btn-error btn-outline btn-sm w-full"
                onClick={() => props.onDelete(r.id)}
              >
                <span class="material-symbols-outlined text-base">delete</span>
                Delete Report
              </button>
            </div>
          </div>
        </RightSheet>
      );
    };
  },
});
